import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'components/ui-components/Modal';
import Button from 'components/ui-components/Button';

import Input from 'components/cef_form/input';
import { WarningTriangle } from 'assets/icons';

import { cefRed, grey5 } from 'constants/variables';

/**
 * @param {Object} props
 * @param {boolean} props.modalIsOpen - whether the modal is open or not
 * @param {function} props.closeModal - function to close modal
 * @param {function} props.stockLevel - product stock level
 * @param {function} props.totalBasketQuantity - input quantity + basket quantity for the product
 * @param {function} props.successCallback - function to execute upon adding to basket
 * @returns back order modal for single product
 */
const BackOrderModal = ({
  modalIsOpen,
  closeModal,
  stockLevel,
  totalBasketQuantity,
  successCallback,
}) => {
  const [isChecked, setChecked] = useState(false);

  const remainingQuantity =
    totalBasketQuantity - stockLevel === 0
      ? totalBasketQuantity
      : totalBasketQuantity - stockLevel;

  useEffect(() => {
    setChecked(false);
  }, [closeModal]);

  return (
    <StyledModal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      config={{ backdropColour: 'rgba(76, 76, 76, 0.9)' }}>
      <div className="fl ai-c">
        <WarningTriangle fillColor={cefRed} width="27" height="30" />
        <h2 className="ml-4 fw-600 ff-body fs-22 mb-0">Insufficient Stock</h2>
      </div>

      <ModalContent>
        <ModalContentDivider className="pb-20 mb-20">
          <p className="fs-16 nm">There is currently {stockLevel} in stock</p>
        </ModalContentDivider>

        {stockLevel > 0 ? (
          <div className="ws-normal">
            <p className="fs-16 lh-1-3">
              <b>{stockLevel}</b> will be dispatched with your order. The
              remaining <b>{remainingQuantity}</b> will be dispatched when
              available. We will notify you with an estimated delivery date.
            </p>
            <p className="fs-16 lh-1-3">
              Cable sold by the metre will be delivered when the full quantity
              is available.
            </p>
          </div>
        ) : (
          <p className="ws-normal">
            This product is currently out of stock and will be dispatched when
            available. We will notify you with an estimated delivery date.
          </p>
        )}
      </ModalContent>

      <ModalFooter>
        <ConfirmationContainer
          isChecked={isChecked}
          className="mb-16 fl fl-row-reverse p-8 ws-normal">
          <Input
            className="back-order__checkbox"
            labelClassName="mb-0 ml-8"
            type="checkbox"
            checked={isChecked}
            handleChange={e => {
              const { checked } = e.target;
              setChecked(checked);
            }}
            id="backorder-label"
            label="I am aware there is insufficient stock and delivery dates will
                be confirmed."
          />
        </ConfirmationContainer>

        <div className="fl jc-sb m-jc-fe">
          <Button
            className="cef-btn cef-btn--grey cef-btn--xs ff-body fw-600"
            handleClick={closeModal}
            testId="bo-modal-cancel">
            Cancel
          </Button>
          <Button
            testId="bo-modal-place-order"
            className={`cef-btn cef-btn--xl ml-16 ff-body fw-600 ${
              isChecked ? 'cef-btn--green' : 'cef-btn--grey-disabled'
            }`}
            handleClick={() => {
              if (isChecked) {
                successCallback();
              }
            }}>
            Place on Back Order
          </Button>
        </div>
      </ModalFooter>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  > div {
    box-sizing: border-box;
    max-width: 550px;
    padding: 2.5rem;
    width: 95%;

    @media screen and (min-width: 768px) {
      width: 75%;
    }

    @media screen and (min-width: 1025px) {
      width: 50%;
    }
  }
`;

const ModalContentDivider = styled.div`
  border-bottom: 1px solid ${grey5};
`;

const ModalContent = styled.div`
  @media screen and (min-width: 768px) {
    margin-left: 2rem;
  }
`;

const ModalFooter = styled.div`
  border-top: 1px solid ${grey5};
  @media screen and (min-width: 768px) {
    margin-left: 2rem;
  }
`;

const ConfirmationContainer = styled.div`
  border: 1px solid ${({ isChecked }) => (!isChecked ? cefRed : grey5)};
`;

export default BackOrderModal;
